import React, { useEffect, useRef, useState } from "react";
import { browserHistory } from "react-router";
import PropTypes from "prop-types";
import LoadingIndicatorHeader from "../loading-indicator/loading-indicator-header";
import PodDetailPod from "./pod-detail-pod";
import PodDetailRules from "./pod-detail-rules";
import PodDetailCampaigns from "./pod-detail-campaigns";
import util from "../util/util";

const jQuery = window.jQuery;

export default function PodDetail(props) {
  const [pod, setPod] = useState({});
  const [id, setId] = useState(0);
  const [status, setStatus] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [rules, setRules] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const timeout = useRef(0);
  // using this time to stop making extra calls to the backend after a Pod Save
  const saveTime = useRef(new Date());

  useEffect(() => {
    const podId = Number(props.params.id);
    // because 'status' doesn't get updated until everything from 'useEffect' is
    // finished, using a different variable 'podStatus' to have the value returned from
    // 'getPod' to be used in 'getPodStatus'
    let podStatus = 0;

    // get the updated pod status;
    const getPodStatus = () => {
      // clear any existing timeout;
      clearTimeout(timeout.current);

      util.getPodStatus(podId).then(data => {
        const awsStatus = data.aws_status;

        // changing the state if there is any Pod status change
        if (podStatus !== awsStatus) {
          setStatus(awsStatus);
          setStatusMessage(data.aws_status_message);
        }

        // no need to send the Notification in the below cases, so marking it as 'seen'
        // as the user is already on the Pod page and sees the Pod status:
        // 1. the Pod is not re-transcoded in AWS - this can happen in 2 cases:
        // a. the Source File/Url/Spots haven't changed
        // b. the Source has changed but has Failed directly,
        // due to a File error (incorrect format, length, video type,...)
        // *extra optimization - if the Status is Ready or Failed only poll the backend
        // for a few initial minutes after the page loads, as the Status cannot change
        // 2. Source changed and Pod is re-transcoded in AWS, and the status changes from
        // 'Processing' -> 'Ready' of 'Failed', while the user is still on the Pod page
        const withinTwoMins = (new Date() - saveTime) / 1000 / 60 < 2;
        const readyOrFailed = podStatus === 2 || podStatus === 3;
        const statusChanged =
          podStatus === 1 && (awsStatus === 2 || awsStatus === 3);

        if ((withinTwoMins && readyOrFailed) || statusChanged) {
          util.getPodsNotifications(podId).then(data => {
            data.results.forEach(result => {
              // toggle the 'Notification seen' in the DB, if it isn't
              if (result.aws_transcode_notification_seen === false) {
                util.savePodNotification(podId, true, result.id);
              }
            });
          });
        }

        // updating 'podStatus' to the new value
        if (podStatus !== awsStatus) {
          podStatus = awsStatus;
        }
      });

      // check again in 30 seconds;
      const delay = 30;

      // start a new timeout;
      timeout.current = setTimeout(() => {
        getPodStatus(podId);
      }, delay * 1000);
    };

    // get the pod data to display;
    const getPod = () => {
      util.getPod(podId).then(data => {
        podStatus = data.aws_status;

        setTimeout(() => {
          setPod(data);
          setId(podId);
          setStatus(podStatus);
          setStatusMessage(data.aws_status_message);
          setRules(data.rules);
          setCampaigns(data.campaigns);
          setLoaded(true);

          // updating the Pod Status (by polling the DB Status)
          getPodStatus();
        }, util.delayDetail);
      });
    };

    getPod();
  }, [props.params.id]);

  useEffect(() => {
    return () => {
      // clear the timeout set by getPodStatus;
      clearTimeout(timeout.current);
    };
  }, []);

  // call the delete pod endpoint
  const deletePod = pod => {
    const title = `Delete Pod`;
    const body = `<p>Are you sure you want to delete the pod: <strong>${pod.name}</strong>?</p>`;
    const buttonTextDefault = `Delete Pod`;
    const buttonTextLoading = `Deleting Pod`;
    const iconClass = `fa-trash-o`;
    const $modal = util.renderModal(
      title,
      body,
      buttonTextDefault,
      buttonTextLoading,
      iconClass
    );
    const $confirmButton = $modal.find(`.btn-danger`);

    // attach temporary event to remove the pod on confirmation of the modal;
    // remove any previous events first;
    // dont namespace the event because we want to remove everything;
    $confirmButton.one(`click`, () => {
      $confirmButton.button(`loading`);

      util
        .deletePod(pod.id)
        .fail(() => {
          setTimeout(() => {
            // reset the button on fail so it can be clicked again;
            $confirmButton.button(`reset`);
          }, util.delayForm);
        })
        .then(() => {
          // once done, go back to the list of pods
          $modal.modal(`hide`);
          browserHistory.push(`/pods/`);
        });
    });

    // attach the modal to the DOM, and then show it;
    jQuery(`body`).append($modal);
    $modal.modal(`show`);
  };

  if (loaded === false) {
    return <LoadingIndicatorHeader />;
  } else {
    return (
      <div className="pod-detail">
        <PodDetailPod
          pod={pod}
          podStatus={status}
          podStatusMessage={statusMessage}
          deletePod={pod => deletePod(pod)}
        />

        <hr className="spacer" />

        <PodDetailRules rules={rules} podId={id} podStatus={status} />

        <hr className="spacer" />

        <PodDetailCampaigns
          campaigns={campaigns}
          podId={id}
          podStatus={status}
        />
      </div>
    );
  }
}

PodDetail.propTypes = {
  params: PropTypes.object
};
